<script setup lang="ts">
import { computed, inject, ref, shallowRef } from 'vue'
import SiteEditableText from '../../SiteEditableText.vue'
import SiteLink from '../../SiteLink.vue'
import { matchesServiceArea } from '../../service-area'
import { serviceAreaContextKey } from '../../service-area-context'
import type { ServiceAreaBlock } from '../../types'

const props = defineProps<{ block: ServiceAreaBlock }>()
const context = inject(serviceAreaContextKey, null)
const query = ref('')
const result = shallowRef<boolean | null>(null)
const serviceAreaSet = computed(() => {
  const id = props.block.content.serviceAreaSetId
  return id ? context?.sets.value.get(id) : undefined
})
const visibleAreas = computed(() => serviceAreaSet.value?.rules.filter((rule) => !rule.isExclusion) ?? [])

function checkArea(): void {
  if (!serviceAreaSet.value || !query.value.trim()) return
  result.value = matchesServiceArea(serviceAreaSet.value, query.value)
}
</script>

<template>
  <section :id="block.settings.anchorId" class="site:bg-(--site-color-background) site:py-16 site:@min-[40rem]:py-24">
    <div
      class="site-site-container"
      :class="block.variant === 'inline' ? 'site:grid site:items-center site:gap-10 site:@min-[64rem]:grid-cols-2 site:@min-[64rem]:gap-16' : undefined"
    >
      <div :class="block.variant === 'card' ? 'site:mx-auto site:max-w-3xl site:text-center' : undefined">
        <SiteEditableText v-if="block.content.eyebrow" v-model="block.content.eyebrow" as="p" class="site:text-sm site:font-semibold site:text-(--site-color-primary)" />
        <SiteEditableText v-model="block.content.heading" as="h2" class="site-heading site:mt-3 site:text-3xl site:font-bold site:tracking-tight site:@min-[40rem]:text-5xl" />
        <SiteEditableText v-model="block.content.description" as="p" class="site:mt-5 site:text-lg site:leading-8 site:text-(--site-color-muted-text)" />
        <div v-if="block.settings.showAreas !== false && visibleAreas.length" class="site:mt-7 site:flex site:flex-wrap site:gap-2" :class="block.variant === 'card' ? 'site:justify-center' : undefined">
          <span v-for="rule in visibleAreas.slice(0, 12)" :key="rule.id" class="site:border site:border-(--site-color-border) site:bg-(--site-color-surface) site:px-3 site:py-1.5 site:text-xs site:font-semibold site:text-(--site-color-muted-text)" :style="{ borderRadius: '999px' }">{{ rule.value }}</span>
        </div>
      </div>

      <div class="site:border site:border-(--site-color-border) site:bg-(--site-color-surface) site:p-6 site:shadow-(--site-shadow-md) site:@min-[40rem]:p-8" :class="block.variant === 'card' ? 'site:mx-auto site:mt-10 site:w-full site:max-w-3xl' : undefined" :style="{ borderRadius: 'var(--site-radius-lg)' }">
        <form v-if="serviceAreaSet" class="site:grid site:gap-4" @submit.prevent="checkArea">
          <label class="site:grid site:gap-2 site:text-sm site:font-semibold">
            {{ block.content.inputLabel }}
            <input v-model="query" type="text" autocomplete="postal-code" required class="site:min-h-12 site:w-full site:border site:border-(--site-color-border) site:bg-(--site-color-background) site:px-4 site:text-base site:font-normal site:text-(--site-color-text) site:outline-none site:transition site:focus:border-(--site-color-primary) site:focus:ring-3 site:focus:ring-(--site-color-primary)/15" :style="{ borderRadius: 'var(--site-radius-sm)' }" @input="result = null" />
          </label>
          <button type="submit" class="site-site-button site:w-full site:justify-center">{{ block.content.checkLabel }}</button>
        </form>

        <div v-else class="site:py-6 site:text-center">
          <p class="site-heading site:text-lg site:font-bold">Service areas are not configured yet.</p>
          <p class="site:mt-2 site:text-sm site:text-(--site-color-muted-text)">Choose a saved service-area set in the website editor.</p>
        </div>

        <div v-if="result !== null && serviceAreaSet" class="site:mt-5 site:border-t site:border-(--site-color-border) site:pt-5" role="status" aria-live="polite">
          <p class="site-heading site:text-lg site:font-bold" :class="result ? 'site:text-(--site-color-primary)' : 'site:text-(--site-color-text)'">{{ result ? serviceAreaSet.successMessage : serviceAreaSet.unavailableMessage }}</p>
          <SiteLink v-if="!result && serviceAreaSet.allowOutsideRequest && block.content.outsideAction" :link="block.content.outsideAction" class="site-site-button site:mt-5">{{ block.content.outsideAction.label }}</SiteLink>
        </div>
      </div>
    </div>
  </section>
</template>
